package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.domain.model.PreMadeCharacter
import fr.manyverse.app.domain.model.PreMadeCharacterToCreate
import fr.manyverse.app.domain.model.PreMadeCharacterToUpdate
import fr.manyverse.app.domain.repository.PreMadeCharacterRepository
import fr.manyverse.app.infrastructure.error.mapToDomainException
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Columns
import io.github.jan.supabase.postgrest.query.Count

private const val PREMADE_CHARACTERS_TABLE = "premade_characters"

class SupabasePreMadeCharacterRepository(
    private val supabaseClient: SupabaseClient
) : PreMadeCharacterRepository {

    override suspend fun getPreMadeCharactersByTableId(
        tableId: String
    ): Result<List<PreMadeCharacter>> {
        return try {
            val items = supabaseClient
                .from(PREMADE_CHARACTERS_TABLE)
                .select(Columns.ALL) {
                    filter { eq("table_id", tableId) }
                }
                .decodeList<PreMadeCharacter>()
            Result.success(items)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun getPreMadeCharacterById(id: String): Result<PreMadeCharacter?> {
        return try {
            val item = supabaseClient
                .from(PREMADE_CHARACTERS_TABLE)
                .select(Columns.ALL) {
                    filter { eq("id", id) }
                }
                .decodeSingleOrNull<PreMadeCharacter>()
            Result.success(item)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun countPreMadeCharactersByTableId(tableId: String): Result<Int> {
        return try {
            val result = supabaseClient
                .from(PREMADE_CHARACTERS_TABLE)
                .select {
                    count(Count.EXACT)
                    filter { eq("table_id", tableId) }
                }
            Result.success((result.countOrNull() ?: 0L).toInt())
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun createPreMadeCharacter(
        preMadeCharacter: PreMadeCharacterToCreate
    ): Result<PreMadeCharacter> {
        return try {
            val created = supabaseClient
                .from(PREMADE_CHARACTERS_TABLE)
                .insert(preMadeCharacter) { select() }
                .decodeSingle<PreMadeCharacter>()
            Result.success(created)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun updatePreMadeCharacter(
        id: String,
        preMadeCharacter: PreMadeCharacterToUpdate
    ): Result<PreMadeCharacter> {
        return try {
            val updated = supabaseClient
                .from(PREMADE_CHARACTERS_TABLE)
                .update(preMadeCharacter) {
                    filter { eq("id", id) }
                    select()
                }
                .decodeSingle<PreMadeCharacter>()
            Result.success(updated)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }

    override suspend fun deletePreMadeCharacter(id: String): Result<Unit> {
        return try {
            supabaseClient
                .from(PREMADE_CHARACTERS_TABLE)
                .delete {
                    filter { eq("id", id) }
                }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "character"))
        }
    }
}
